/**
 * Google Analytics 4 / Google Ads tag — runtime helpers.
 *
 * The gtag.js script + Consent Mode v2 defaults + production-host /
 * Do-Not-Track gating live in `public/index.html` so Google Ads' static
 * crawler can detect them. This module provides the JS-runtime helpers
 * for tracking SPA route changes and Ads conversion events on top of
 * the already-bootstrapped `window.gtag`.
 *
 * Why split it this way:
 *   • Static <script> in index.html → satisfies Ads "Test connection"
 *     (the crawler does not execute JS, so dynamic injection failed).
 *   • This module → safe no-ops when gtag wasn't configured (preview /
 *     DNT / consent-denied), so we can call it from any page handler
 *     without an `if (production)` guard everywhere.
 *
 * Public API:
 *   initAnalytics()              — back-compat shim (no-op now).
 *   trackPageView(path, title)   — emit a page_view on route change.
 *   trackEvent(name, params)     — generic `gtag('event', …)` wrapper.
 *   trackSignupCompleted(args)   — Ads conversion: signup_completed.
 *   trackSubscriptionStarted(...) → GA4 `purchase` + subscription_started.
 *   trackPromoCtaClicked(args)   — promo_cta_clicked + select_promotion.
 *   grantAnalyticsConsent()      — flip Consent Mode to granted (post opt-in).
 *   revokeAnalyticsConsent()     — flip back to denied.
 */

// True only when the inline snippet in index.html actually ran the
// production-host check and called gtag('config', …).
function _isConfigured() {
  return typeof window !== 'undefined' && Boolean(window.__gaConfigured) && typeof window.gtag === 'function';
}

/** Back-compat — gtag is now bootstrapped from index.html. */
export function initAnalytics() {
  // Intentionally empty. Retained so callers don't have to change.
}

/** Track a SPA route change. Safe no-op when GA isn't configured. */
export function trackPageView(path, title) {
  if (!_isConfigured()) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || (typeof document !== 'undefined' ? document.title : ''),
    page_location: typeof window !== 'undefined' ? window.location.href : '',
  });
}

/** Generic event helper. Safe no-op when GA isn't configured. */
export function trackEvent(name, params = {}) {
  if (!_isConfigured() || !name) return;
  try { window.gtag('event', name, params); } catch { /* silent */ }
}

/** Call after the visitor accepts marketing/analytics cookies. */
export function grantAnalyticsConsent() {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  });
}

/** Call when the visitor revokes consent. */
export function revokeAnalyticsConsent() {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Conversion events for Google Ads
// ─────────────────────────────────────────────────────────────────────────

/** A new account was created (any signup path — form, Google OAuth, etc.). */
export function trackSignupCompleted({ method = 'email', plan = 'free' } = {}) {
  trackEvent('sign_up', { method });
  // Custom event name so Google Ads can import it as a conversion.
  trackEvent('signup_completed', { method, plan });
}

/**
 * A paid plan was activated. Emits GA4-standard `purchase` so it lines up
 * with Google Ads' "Purchase" conversion template out-of-the-box.
 *
 * @param {object} args
 * @param {string} args.planId       e.g. "starter" | "pro" | "elite"
 * @param {number} args.amountPaise  Razorpay returns paise. We convert to ₹.
 * @param {string} [args.currency]   Default "INR".
 * @param {string} [args.txnId]      Razorpay payment id (for de-duplication).
 */
export function trackSubscriptionStarted({ planId, amountPaise, currency = 'INR', txnId } = {}) {
  if (!planId || !amountPaise) return;
  const value = Math.round(amountPaise) / 100;
  const params = {
    transaction_id: txnId || undefined,
    value,
    currency,
    items: [{
      item_id: `plan_${planId}`,
      item_name: `${String(planId).toUpperCase()} subscription`,
      item_category: 'subscription',
      price: value,
      quantity: 1,
    }],
  };
  trackEvent('purchase', params);
  trackEvent('subscription_started', { plan: planId, value, currency, transaction_id: txnId });
}

/** Visitor clicked any promo-campaign CTA. */
export function trackPromoCtaClicked({ source = 'unknown', campaign = 'premium_launch_june_2026' } = {}) {
  trackEvent('promo_cta_clicked', { source, campaign });
  // Also emit GA4's "select_promotion" so it shows in standard promo reports.
  trackEvent('select_promotion', {
    promotion_id: campaign,
    promotion_name: 'June 2026 Free Premium Access',
    creative_slot: source,
  });
}
